import { inviteFirstOrgAdmin } from '@jq4race/api-client';
import { Trans, useTranslation } from '@jq4race/i18n';
import type { OrganizationResponse } from '@jq4race/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@jq4race/ui';
import { useEffect, useState } from 'react';

import { translateApiError } from '../../../../shared/errors/translateApiError';

interface InviteOrgAdminDialogProps {
  org: OrganizationResponse | null;
  onOpenChange: (open: boolean) => void;
  onInvited: (email: string) => void;
}

export function InviteOrgAdminDialog({ org, onOpenChange, onInvited }: InviteOrgAdminDialogProps) {
  const { t } = useTranslation('organizations');
  const open = org !== null;
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    if (!org) return;
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await inviteFirstOrgAdmin(org.id, { email });
      onInvited(email);
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof Error
          ? translateApiError(err, t, { context: 'platform' })
          : t('inviteOrgAdminDialog.error'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('inviteOrgAdminDialog.title')}</DialogTitle>
          <DialogDescription>
            {org ? (
              <Trans
                t={t}
                i18nKey="inviteOrgAdminDialog.description"
                values={{ name: org.name }}
                components={{ strong: <strong className="font-medium text-jq-charcoal" /> }}
              />
            ) : null}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="invite-org-admin-email">{t('inviteOrgAdminDialog.emailLabel')}</Label>
            <Input
              id="invite-org-admin-email"
              type="email"
              required
              autoFocus
              placeholder={t('inviteOrgAdminDialog.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          </div>

          {error && (
            <p className="rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-3 py-2 text-[13px] text-jq-danger">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              {t('inviteOrgAdminDialog.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? t('inviteOrgAdminDialog.submitting') : t('inviteOrgAdminDialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
